import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge, Card, Group, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import { IconCalendarClock } from '@tabler/icons-react';
import type { DeadlineUrgency, UpcomingDeadline } from '@/lib/dashboard';
import { ROUTES } from '@/routes/paths';
import { STATUS } from '@/lib/statusColors';
import classes from './homeRow.module.css';

// Один форматтер на модуль (Intl-конструктор дорогой). «ДД.ММ».
const dayMonth = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit' });

// Срочность сроком: сегодня — danger (горит), завтра — waiting/пшеничный (скоро), дальше — нейтральный.
const URGENCY_COLOR: Readonly<Record<DeadlineUrgency, string>> = {
  today: STATUS.danger,
  tomorrow: STATUS.waiting,
  date: STATUS.neutral,
};

interface UpcomingDeadlinesProps {
  readonly items: readonly UpcomingDeadline[];
}

// Ближайшие плановые сроки по сделкам — заранее предупредить о подходящей отгрузке.
export default function UpcomingDeadlines({ items }: UpcomingDeadlinesProps) {
  const { t } = useTranslation();

  return (
    <Card withBorder padding={0}>
      <Stack gap={0}>
        {items.map((item) => {
          const badgeLabel =
            item.urgency === 'today'
              ? t('app.home.deadlines.today')
              : item.urgency === 'tomorrow'
                ? t('app.home.deadlines.tomorrow')
                : dayMonth.format(new Date(item.dateMs));
          return (
            <UnstyledButton
              key={item.dealId}
              component={Link}
              to={`${ROUTES.APP_DEALS}/${item.dealId}`}
              className={classes.row}
            >
              <Group align="center" wrap="nowrap" gap="sm">
                <ThemeIcon variant="light" color={URGENCY_COLOR[item.urgency]} size="md" radius="xl">
                  <IconCalendarClock size={16} />
                </ThemeIcon>
                <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={600} size="sm" truncate>
                    {t('app.home.deal', { no: item.dealNo })}
                    {item.goods ? ` · ${item.goods}` : ''}
                  </Text>
                </Stack>
                <Badge color={URGENCY_COLOR[item.urgency]} variant="light" size="sm" style={{ flexShrink: 0 }}>
                  {badgeLabel}
                </Badge>
              </Group>
            </UnstyledButton>
          );
        })}
      </Stack>
    </Card>
  );
}
